import React, { useState } from "react";

import { PlatformSubscriptionRow } from "../../../common/platformTypes";
import { BoolCheck } from "../../../components/BoolCheck";
import { Code } from "../../../components/Code";
import { Date as DateView } from "../../../components/Date";
import { PlatformUserLink, TeamLink } from "../../../components/DocumentLink";
import { ExpanderContent, ExpanderToggle } from "../../../components/Expander";

const asDate = (iso?: string): Date | undefined =>
  iso ? new Date(iso) : undefined;

/** The id is the fallback, not the label — the link still copies the id. */
const nameOf = (
  subscription: PlatformSubscriptionRow,
  userId: string,
): string => subscription.userNamesById?.[userId] ?? userId;

/** Booleans show as the bare key; numerics carry their value. */
const formatOverrides = (
  overrides?: Record<string, boolean | number>,
): string =>
  Object.entries(overrides ?? {})
    .map(([key, value]) =>
      typeof value === "number" ? `${key}:${value}` : key,
    )
    .join(", ");

export const PlatformSubscriptionDetails: React.FunctionComponent<{
  subscription: PlatformSubscriptionRow;
}> = ({ subscription }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <table className="table table-sm table-bordered table-hover">
        <tbody>
          <tr>
            <th style={{ width: "22%" }}>ID</th>
            <td className="text-monospace">{subscription._id}</td>
          </tr>
          <tr>
            <th>Team</th>
            <td>
              <TeamLink docId={subscription.teamId}>
                {subscription.teamName}
              </TeamLink>
            </td>
          </tr>
          {subscription.payment?.stripeCustomerId && (
            <tr>
              <th>Stripe Customer ID</th>
              <td>
                {subscription.payment.stripeCustomerId}{" "}
                <a
                  href={`https://dashboard.stripe.com/customers/${subscription.payment.stripeCustomerId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-primary ml-2"
                >
                  Stripe Record
                </a>
              </td>
            </tr>
          )}
          {subscription.payment?.stripeSubscriptionItemId && (
            <tr>
              <th>Stripe Subscription Item ID</th>
              <td className="text-monospace">
                {subscription.payment.stripeSubscriptionItemId}
              </td>
            </tr>
          )}
          <tr>
            {/* `entitled`, not the raw `active` flag: a plan Stripe has not
                closed out yet stays active past its end date. */}
            <th>Active</th>
            <td>
              <BoolCheck check={subscription.entitled} />
              {subscription.active && !subscription.entitled && (
                <span className="text-muted ml-2">
                  flagged active, but past its end date
                </span>
              )}
            </td>
          </tr>
          <tr>
            <th>Plan</th>
            <td>{subscription.tier}</td>
          </tr>
          <tr>
            {/* The subscription's own owner — not the team's ownerId. */}
            <th>Owner</th>
            <td>
              {subscription.subscribingUserId ? (
                <PlatformUserLink
                  docId={subscription.subscribingUserId}
                  scope="owner"
                >
                  {nameOf(subscription, subscription.subscribingUserId)}
                </PlatformUserLink>
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
          </tr>
          <tr>
            <th>Billing Admins</th>
            <td>
              {subscription.billingAdminIds.length ? (
                subscription.billingAdminIds.map((id) => (
                  <PlatformUserLink key={id} docId={id} scope="billing">
                    {nameOf(subscription, id)}
                  </PlatformUserLink>
                ))
              ) : (
                <span className="text-muted">—</span>
              )}
            </td>
          </tr>
          <tr>
            <th>Start Date</th>
            <td>
              <DateView date={asDate(subscription.startDate)} />
            </td>
          </tr>
          <tr>
            <th>End Date</th>
            <td>
              <DateView date={asDate(subscription.endDate)} />
            </td>
          </tr>
          <tr>
            <th>Seats / Licenses</th>
            <td>
              {subscription.seatCount} / {subscription.licenses ?? "—"}
              {subscription.licenses !== undefined &&
                subscription.seatCount > subscription.licenses && (
                  <span className="text-danger ml-2">over quota</span>
                )}
            </td>
          </tr>
          <tr>
            <th>Trial</th>
            <td>
              <BoolCheck check={subscription.isTrial} />
            </td>
          </tr>
          <tr>
            <th>One Time</th>
            <td>
              <BoolCheck check={!!subscription.isOneTime} />
            </td>
          </tr>
          <tr>
            <th>Memo</th>
            <td>
              {subscription.memo ?? <span className="text-muted">—</span>}
            </td>
          </tr>
          <tr>
            <th>Feature Overrides</th>
            <td>
              {formatOverrides(subscription.featureOverrides) || (
                <span className="text-muted">—</span>
              )}
            </td>
          </tr>
        </tbody>
      </table>
      View raw json{" "}
      <ExpanderToggle
        expanded={expanded}
        toggle={() => setExpanded(!expanded)}
      />
      <ExpanderContent expanded={expanded}>
        <Code object={subscription} />
      </ExpanderContent>
    </div>
  );
};
